/*
  Tapticle stats count-up — vanilla, deferred. When a [data-tt-countup]
  number scrolls into view it animates from 0 to its set value once,
  preserving the original format (thousands separators, decimal places).
  The accent suffix (+, ★, etc.) is a sibling and is left untouched.
  Under prefers-reduced-motion the final value is shown instantly.
*/
(() => {
  'use strict';

  const els = document.querySelectorAll('[data-tt-countup]');
  if (!els.length) return;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Parse a display value like "1,200" or "4.9" into target + format hints.
  const parse = (raw) => {
    const text = raw.trim();
    const hasComma = text.indexOf(',') !== -1;
    const numeric = parseFloat(text.replace(/,/g, ''));
    const dot = text.indexOf('.');
    const decimals = dot === -1 ? 0 : text.length - dot - 1;
    return { target: isNaN(numeric) ? null : numeric, decimals, hasComma };
  };

  const format = (value, { decimals, hasComma }) => {
    const fixed = value.toFixed(decimals);
    if (!hasComma) return fixed;
    const parts = fixed.split('.');
    parts[0] = parseInt(parts[0], 10).toLocaleString('en-GB');
    return parts.join('.');
  };

  const run = (el) => {
    const info = parse(el.textContent);
    if (info.target === null) return; // non-numeric (leave as-is)
    if (reduced) { el.textContent = format(info.target, info); return; }

    // Reserve the final width so the suffix/label don't shift while the
    // number grows from 0 (keeps CLS ~0).
    el.style.display = 'inline-block';
    el.style.textAlign = 'right';
    el.style.minWidth = Math.ceil(el.getBoundingClientRect().width) + 'px';

    const duration = 1400;
    const start = performance.now();
    const ease = (t) => 1 - Math.pow(1 - t, 3); // easeOutCubic
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      el.textContent = format(info.target * ease(t), info);
      if (t < 1) requestAnimationFrame(tick);
      else el.textContent = format(info.target, info);
    };
    requestAnimationFrame(tick);
  };

  if (reduced || !('IntersectionObserver' in window)) {
    els.forEach(run);
    return;
  }

  const io = new IntersectionObserver(
    (entries, observer) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        run(entry.target);
        observer.unobserve(entry.target); // once only
      }
    },
    { threshold: 0.5 }
  );
  els.forEach((el) => io.observe(el));
})();
